import React, { useEffect, useState } from 'react';
import { Tooltip, Button, Loader, Box } from '@wix/design-system';
import type { IDetailsProps } from './solution-info-details';
import { biLogger } from '../../../../bi';
import { addAppClick, openAppClick } from '@wix/bi-logger-market/v2';
import { config } from '../../../../config';
import { composer } from '../../../../component-decorators/composer';
import {
  getPathForBi,
  getReferralInfo,
  getWidgetReferral,
} from '../../../../common/bi-events';
import { MarketPermissions, Path } from '../../../..';
import {
  openOrAddApp,
  getInstalledApps,
  isBundleAppAndNotInstalled,
  convertOriginToOriginMI,
  isBundleAppWithVoucher,
} from '../../../../common/utils';
import { Origin } from '../../../../enums/marketplace-enums';
import { useConfigContext } from '../../../../config-context';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import { shouldShowAddAgain } from '@wix/manage-installed-apps';
import { getPermittedActions, getWixOfferingId } from '../../../../api/api';
import type { BundleWebSolution } from '@wix/ambassador-marketplace/types';
import type { WebSolutionExtended } from '@wix/app-market-collection-widget';
import type { MarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import type { IBiData } from '../../../../types/common/marketplace';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import type { IAppDependenciesProps } from '../solution-page';
import { IncompleteSetupReason } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';

export interface IAddToSiteProps
  extends Partial<WithTranslationProps>,
    Partial<InjectedExperimentsProps> {
  marketplaceStore?: MarketplaceStore;
  shouldShowTooltip: boolean;
  addToSiteData: IDetailsProps;
  size: any;
  isPermitted?: boolean;
  text?: string;
  location?: string;
  isAppInstalled?: boolean;
  appDependencies?: IAppDependenciesProps;
}
export const AddToSite = composer()
  .withMarketplaceStore()
  .withTranslation()
  .compose(
    ({
      shouldShowTooltip,
      addToSiteData,
      t,
      marketplaceStore,
      size,
      location,
      appDependencies,
    }: IAddToSiteProps) => {
      const isAppInstalled = !!(
        marketplaceStore.installedApps &&
        marketplaceStore.installedApps.findIndex(
          (installedApp) =>
            installedApp.appId === addToSiteData.id &&
            installedApp.installationDetails.incompleteSetupReason !==
              IncompleteSetupReason.PENDING_REQUIRED_INSTALLATIONS,
        ) > -1
      );
      const isAddAgainApp =
        isAppInstalled &&
        shouldShowAddAgain(
          marketplaceStore.installedApps?.find(
            (app) => app.appId === addToSiteData.id,
          ),
          convertOriginToOriginMI(config.origin),
        );
      const btnText = getButtonText(
        isAppInstalled,
        !!isAddAgainApp,
        t,
        addToSiteData.id,
        marketplaceStore.bundleApps,
        addToSiteData.showRegularAppPage,
      );

      const [isLoading, setIsLoading] = React.useState(true);

      React.useEffect(() => {
        if (
          marketplaceStore.metaSiteId &&
          !marketplaceStore?.permissions?.length
        ) {
          getPermittedActions(marketplaceStore.metaSiteId).then((value) => {
            marketplaceStore.setPermissions(value);
            setIsLoading(false);
          });
        } else {
          setIsLoading(false);
        }
      }, [marketplaceStore.permissions]);

      const permitted = isPermitted(
        isAppInstalled,
        marketplaceStore.permissions,
      );
      if (isLoading) {
        return (
          <Box align="center">
            <Loader size="tiny" />
          </Box>
        );
      }
      return (
        <AddToSiteBtn
          addToSiteData={addToSiteData}
          text={btnText}
          isPermitted={permitted}
          size={size}
          location={location}
          isAppInstalled={isAppInstalled}
          appDependencies={appDependencies}
          shouldShowTooltip={shouldShowTooltip}
        />
      );
    },
  );
export const AddToSiteBtn = composer()
  .withMarketplaceStore()
  .withExperiments()
  .withTranslation()
  .compose(
    ({
      addToSiteData,
      t,
      marketplaceStore,
      size,
      isPermitted,
      text,
      location,
      isAppInstalled,
      experiments,
      appDependencies,
      shouldShowTooltip,
    }: IAddToSiteProps) => {
      const configContext = useConfigContext();
      const updateInstalledApps = async () => {
        const signedInstance = configContext.getAppInstance(
          '22bef345-3c5b-4c18-b782-74d4085112ff',
        );
        const installedApps = await getInstalledApps(
          signedInstance,
          configContext.locale,
        );
        marketplaceStore.setInstalledApps(installedApps);
      };
      const [isDisabled, setIsDisabled] = useState<boolean>(true);
      const shouldShowAppDependencies = origin !== Origin.STANDALONE;
      const shouldAllowInstall =
        appDependencies?.showAppDependencyHelper === 'None';
      useEffect(() => {
        const notPermitted = !isPermitted;
        const notAllowedInstall =
          shouldShowAppDependencies && !shouldAllowInstall;
        setIsDisabled(
          notPermitted ||
            notAllowedInstall ||
            addToSiteData.isNotAvailableToInstall,
        );
      }, [appDependencies]);
      const noAppRequirements = shouldShowAppDependencies && shouldAllowInstall;
      function getTooltipText() {
        if (addToSiteData.isNotAvailableToInstall) {
          return 'solution.page.isNotAvailable.tooltip';
        }
        if (!shouldShowAppDependencies) {
          return 'marketplace.actions.tooltip';
        }
        if (
          shouldShowAppDependencies &&
          appDependencies?.needToInstallApps?.length === 1 &&
          appDependencies?.showAppDependencyHelper === 'App-Req-Installation'
        ) {
          return 'solution.page.appDependencyNotInstalled.tooltip';
        }
        if (
          shouldShowAppDependencies &&
          appDependencies?.needToInstallApps?.length > 1 &&
          appDependencies?.showAppDependencyHelper === 'App-Req-Installation'
        ) {
          return 'solution.page.appDependenciesNotInstalled.tooltip';
        }
        if (
          shouldShowAppDependencies &&
          appDependencies?.showAppDependencyHelper === 'App-Req-Pending'
        ) {
          return 'solution.page.appDependencyPending.tooltip';
        }
        return 'marketplace.actions.tooltip';
      }
      const tooltipText = getTooltipText();

      const [wixOffering, setWixOffering] = useState<any>(undefined);

      useEffect(() => {
        if (
          config.origin !== Origin.STANDALONE &&
          addToSiteData?.showRegularAppPage
        ) {
          getWixOfferingId(addToSiteData.id).then((WixOfferingResponse) => {
            setWixOffering(
              WixOfferingResponse.data?.appComponents?.[0]?.components?.[0]
                ?.compData?.wixOffering,
            );
          });
        } else {
          setWixOffering(addToSiteData?.showRegularAppPage);
        }
      }, [addToSiteData?.showRegularAppPage]);
      const wixOfferingToastText = t('wix-offering.toast-text');
      const wixOfferingToastCtaText = t('wix-offering.toast-cta');

      return (
        <Tooltip
          disabled={
            shouldShowTooltip && addToSiteData.isNotAvailableToInstall
              ? false
              : shouldShowAppDependencies && !shouldAllowInstall
              ? isPermitted && noAppRequirements
              : isPermitted
          }
          content={t(tooltipText, {
            appRequirement: appDependencies?.needToInstallApps?.[0]?.name,
            editorType:
              config.editorType === 'EDITORX'
                ? t('modals-notSupportedInEditorX')
                : config.editorType === 'STUDIO'
                ? t('modals-notSupportedStudio')
                : '',
          })}
        >
          <span>
            <Button
              dataHook="btn-install-cta"
              onClick={() =>
                onAddToSiteClick(
                  addToSiteData,
                  configContext,
                  updateInstalledApps,
                  location,
                  isAppInstalled,
                  wixOffering,
                  wixOfferingToastText,
                  wixOfferingToastCtaText,
                  appDependencies?.needToInstallApps,
                )
              }
              size={size}
              disabled={isDisabled}
            >
              {text}
            </Button>
          </span>
        </Tooltip>
      );
    },
  );
const getButtonText = (
  isAppInstalled: boolean,
  isAddAgainApp: boolean,
  t,
  appID: string,
  bundleApps: BundleWebSolution[],
  isWixOffering?: boolean,
) => {
  if (
    isBundleAppWithVoucher(appID, bundleApps) &&
    isBundleAppAndNotInstalled(appID, bundleApps)
  ) {
    return t('solution.page.voucher');
  }
  if (isBundleAppAndNotInstalled(appID, bundleApps)) {
    return t('solution.page.claim');
  }
  if (isAppInstalled && isAddAgainApp) {
    return t('solution.page.addAgain');
  }
  if (isAppInstalled || isWixOffering) {
    return t('solution.page.open');
  }
  return t('solution.page.addToSite');
};
function sendAddToSiteBi(
  biData,
  location,
  route,
  solutionBiParams: IBiData,
  prevRoute,
  requiredApps,
) {
  biLogger.report(
    addAppClick({
      has_app_requirement: requiredApps?.appDependencies?.length,
      app_id: biData?.devNameClickData?.app_id,
      category: biData?.category,
      pageType: 'solution_page',
      slug_id: route?.referralSectionName,
      market: solutionBiParams?.market,
      tag_type: solutionBiParams?.tagType,
      tag_name: solutionBiParams?.tagName,
      referral_info: solutionBiParams?.referralInfo,
      search_term: solutionBiParams?.searchTerm,
      widget_referral: solutionBiParams?.widgetReferral,
      collimp_id: solutionBiParams?.collimp_id,
      referral_section_name: solutionBiParams?.referralSectionName,
      section: solutionBiParams?.section,
      navigation_source: route?.searchLocation,
      referral_tag_type: getPathForBi(prevRoute?.path),
      referral_sub_tag_name: prevRoute?.subCategories?.join(','),
    }),
  );
}

function sendOpenAppBi(biData, location, route, solutionBiParams: IBiData) {
  biLogger.report(
    openAppClick({
      app_id: biData?.devNameClickData?.app_id,
      category: biData?.category,
      market: solutionBiParams?.market,
      tag_type: solutionBiParams?.tagType,
      tag_name: solutionBiParams?.tagName,
      referral_info: solutionBiParams?.referralInfo,
      search_term: solutionBiParams?.searchTerm,
      section: solutionBiParams?.section,
    }),
  );
}

export const onAddToSiteClick = async (
  props,
  configContext,
  updateInstalledApps,
  location,
  isAppInstalled,
  wixOfferingId,
  wixOfferingToastText,
  wixOfferingToastCtaText,
  requiredApps,
) => {
  const appForInstallation: WebSolutionExtended = {
    id: props.id,
    pricing: props.pricing,
    openConsentIn: props.openConsentIn,
    name: props.name,
    teaser: props.teaser,
    icon: props.icon,
    description: props.description,
    company: props.company,
    Tags: props.Tags,
  };
  const { route, prevRoute } = props.marketplaceStore;
  const solutionBiParams: IBiData = {
    market: config.originForBI,
    tagType: getPathForBi(Path.WEB_SOLUTION),
    tagName: props.biData.devNameClickData.app_id,
    referralInfo: getReferralInfo(route?.referral, prevRoute),
    searchTerm: route?.query || prevRoute?.query,
    widgetReferral: getWidgetReferral(),
    collimp_id: route?.collimp_id,
    referralSectionName: route?.referralSectionName || route?.referral,
    section: route?.section || null,
  };
  if (isAppInstalled) {
    sendOpenAppBi(props.biData, location, route, solutionBiParams);
  } else {
    sendAddToSiteBi(
      props.biData,
      location,
      route,
      solutionBiParams,
      prevRoute,
      requiredApps,
    );
  }
  await openOrAddApp(
    appForInstallation,
    props.marketplaceStore,
    solutionBiParams,
    configContext,
    props.experiments,
    updateInstalledApps,
    wixOfferingId,
    wixOfferingToastText,
    wixOfferingToastCtaText,
  );
};
const isPermitted = (
  isAppInstalled: boolean,
  permissions: string[],
): boolean => {
  if (config.origin === Origin.STANDALONE) {
    return true;
  }
  return (
    isAppInstalled ||
    permissions?.some((p) => p === MarketPermissions.INSTALL_APP)
  );
};
